/**
 * Error Handler - Centralized error handling utilities
 *
 * RULES:
 * 1. ALWAYS use handleError() in catch blocks for consistent logging + message extraction
 * 2. ALWAYS use extractErrorMessage() when you only need the message (no logging)
 * 3. Use createErrorHandler() to create reusable handlers for components
 * 4. NEVER write `err instanceof Error ? err.message : 'fallback'` manually
 *
 * extractErrorMessage is re-exported from httpUtils for convenience.
 */

import { extractErrorMessage } from './httpUtils';

// Re-export for convenience - single import point for error handling
export { extractErrorMessage };

/**
 * Handles async operation errors consistently.
 * Logs the error with context and returns a user-friendly message.
 *
 * @param err - The caught error (unknown type from catch block)
 * @param context - Descriptive label for where the error occurred (e.g., 'VoiceUpload', 'VideoGeneration')
 * @param defaultMessage - Optional fallback message if error cannot be extracted
 * @returns User-friendly error message string
 *
 * @example
 * ```ts
 * catch (err) {
 *   const message = handleError(err, 'VideoGeneration');
 *   setError(message);
 * }
 * ```
 */
export function handleError(err: unknown, context: string, defaultMessage?: string): string {
  const message = extractErrorMessage(err, defaultMessage);
  console.error(`[${context}] ${message}`, err);
  return message;
}

/**
 * Detect whether an auth error was caused by the user cancelling/denying the flow
 * (rather than a genuine failure). Covers both the desktop loopback OAuth flow
 * (Google returns the spec code `access_denied`) and the web popup flow (Firebase
 * codes `auth/popup-closed-by-user`, `auth/cancelled-popup-request`,
 * `auth/user-cancelled`). These are protocol-level identifiers, not localized text,
 * so matching on them is stable across locales.
 *
 * @param err - The caught error (unknown type from catch block)
 * @returns true if the user cancelled/denied the sign-in
 */
export function isUserCancelledAuth(err: unknown): boolean {
  const raw = extractErrorMessage(err, '').toLowerCase();
  return (
    raw.includes('access_denied') ||
    raw.includes('popup-closed-by-user') ||
    raw.includes('cancelled-popup-request') ||
    raw.includes('user-cancelled') ||
    raw.includes('popup_closed') ||
    raw.includes('user_cancelled')
  );
}

/**
 * Creates a type-safe error handler for use in catch blocks.
 * Returns a function that logs the error and calls onError with the message.
 *
 * @param context - Descriptive label for error logging
 * @param onError - Callback to receive the extracted error message
 * @returns Error handler function accepting unknown error
 *
 * @example
 * ```ts
 * const onCatchError = createErrorHandler('VideoGeneration', setError);
 *
 * try {
 *   await generateVideo();
 * } catch (err) {
 *   onCatchError(err);
 * }
 * ```
 */
export function createErrorHandler(
  context: string,
  onError: (message: string) => void
): (err: unknown) => void {
  return (err: unknown) => {
    const message = handleError(err, context);
    onError(message);
  };
}
